import { useQuery } from "@tanstack/react-query";
import { useGet } from "../../../common/api-service";
import { UserModel } from "../../users-facility/api/types";
import useMainStore from "./store";
import { Handlers } from "./handlers";
import { ProductListModel } from "../../products/api/types";
import { PrivacyAgreementModel } from "../components/privacy-statement/api/types";
import usePrivacyStore from "../components/privacy-statement/api/privacy-store";
import { useNavigate } from "react-router-dom";
import { c } from "framer-motion/dist/types.d-6pKw1mTI";
import { useAuthStore } from "../../../common/auth/store";
import { decryptList } from "../../../common/utils/decryption-service";
import { useSuspendedModalStore } from "../../home/suspended-modal/api/suspended-modal-store";

export const useGetMyProfile = () => {
  const { get } = useGet();
  const mainStore = useMainStore();
  const { setIsSuspendedModalOpen } = useSuspendedModalStore();
  const handlers = new Handlers(mainStore);
  const url = `/api/users`;
  const navigate = useNavigate();
  return useQuery<UserModel>({
    queryFn: async () => {
      const response = await get<UserModel>(url);
      handlers.handleSetCurrentUser(response);
      // const atleastOneSuspended = response.productList?.some(
      //   (x) => x.isSuspended === true,
      // );
      // if (atleastOneSuspended) {
      //   setIsSuspendedModalOpen(true);
      //   return response;
      // }

      if (!response.birthDate || response.isDefaultPassword) {
        navigate("/edit-profile");
      }
      return response;
    },
    queryKey: [url],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetMyPrivileges = () => {
  const { get } = useGet();
  const url = "/api/privileges";
  const authStore = useAuthStore();
  const navigate = useNavigate();

  return useQuery<string[]>({
    queryFn: async () => {
      const response = await get<string[]>(url);
      localStorage.setItem("pv01", JSON.stringify(response));

      const decryptedUserPrivileges = decryptList(response);
      authStore.setPrivilege(decryptedUserPrivileges);
      if (
        decryptedUserPrivileges.includes("UP001") ||
        decryptedUserPrivileges.includes("UP002")
      ) {
        navigate("/edit-profile");
      }
      return response;
    },
    queryKey: [url],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetProducts = () => {
  const { get } = useGet();
  const { selectedClient, initialProductState, currentUser } = useMainStore();
  const { pageNumber, pageSize, searchText, onlyActive } = initialProductState;

  let url = `/api/products/list?PageNumber=${pageNumber}&PageSize=${pageSize}`;

  if (searchText) {
    url += `&SearchText=${encodeURIComponent(searchText)}`;
  }

  if (onlyActive) {
    url += `&OnlyActive=true`;
  }

  if (selectedClient?.id) {
    url += `&ClientId=${encodeURIComponent(selectedClient.id)}`;
  }

  return useQuery<ProductListModel, Error>({
    queryKey: [
      "connected-apps",
      pageNumber,
      pageSize,
      searchText ?? "",
      onlyActive,
      selectedClient?.id ?? "none",
    ],
    queryFn: () => get<ProductListModel>(url),
    enabled: !!currentUser,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetUserPrivacyAgreement = () => {
  const { get } = useGet();
  const { setOpenPrivacyModal } = usePrivacyStore();
  var url = `/api/user-privacy-statement/get-user-privacy-agreement`;

  return useQuery<PrivacyAgreementModel>({
    queryFn: async () => {
      const response = await get<PrivacyAgreementModel>(url);
      if (
        response.isAgreeOfPersonalData &&
        response.isAgreeOfPrivacyStatement &&
        response.isAgreeOfTermsAndCondition
      ) {
        setOpenPrivacyModal(false);
      } else {
        setOpenPrivacyModal(true);
      }
      return response;
    },
    queryKey: [url],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  });
};
